import { useEffect, useRef, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AuthContext } from './AuthContext';
import { ThemeContext } from './ThemeContext';
import { useApiData, useApiMutation } from '../api/hooks';

const PREFS_ENDPOINT = '/api/v1.0/preferences';
const SAVE_DEBOUNCE_MS = 500;

/**
 * Syncs user UI preferences (dark mode, audio, fullscreen) between
 * the server database and ThemeContext so settings persist across devices.
 *
 * - On login: fetches preferences from API and applies them via loadPreferences()
 * - On preference changes: debounces and PUTs to API (fire-and-forget)
 * - Falls back gracefully to localStorage if the API is unavailable
 */
const PreferencesSyncProvider = ({ children }) => {
	const { state: authState } = useContext(AuthContext);
	const { state: themeState, actions: { loadPreferences } } = useContext(ThemeContext);

	// Prevents saving back to the server immediately after a server-load overwrites local state
	const justLoadedFromServer = useRef(false);
	const saveTimerRef = useRef(null);

	const onPrefsLoaded = useCallback((data) => {
		justLoadedFromServer.current = true;
		// ManualAgentOrder is stored server-side as a JSON array of agent ids; parse defensively.
		let manualAgentOrder = [];
		if (data.manualAgentOrder) {
			try {
				const parsed = JSON.parse(data.manualAgentOrder);
				if (Array.isArray(parsed)) manualAgentOrder = parsed;
			} catch { manualAgentOrder = []; }
		}
		loadPreferences({
			darkMode: data.darkMode,
			audioEnabled: data.audioEnabled,
			fullscreenMode: data.fullscreenMode,
			tileSize: data.tileSize || 'normal',
			showAlertTray: data.showAlertTray !== false,
			viewMode: data.viewMode || 'tiles',
			tileSortOrder: data.tileSortOrder || 'manual',
			tileColumns: data.tileColumns != null ? data.tileColumns : 3,
			tileMinHeight: data.tileMinHeight != null ? data.tileMinHeight : 0,
			manualAgentOrder
		});
	}, [loadPreferences]);

	// Load preferences from server — null endpoint when not logged in skips the fetch
	useApiData(
		authState.loggedIn ? PREFS_ENDPOINT : null,
		[authState.loggedIn],
		{ onSuccess: onPrefsLoaded }
	);

	const { mutate: savePrefs } = useApiMutation(PREFS_ENDPOINT, 'PUT');

	// Save preferences to server when they change (debounced)
	useEffect(() => {
		if (!authState.loggedIn) return;

		// Skip the save that would immediately follow a server-initiated load
		if (justLoadedFromServer.current) {
			justLoadedFromServer.current = false;
			return;
		}

		if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

		saveTimerRef.current = setTimeout(() => {
			savePrefs({
				darkMode: themeState.darkMode,
				audioEnabled: themeState.audioEnabled,
				fullscreenMode: themeState.fullscreen,
				tileSize: themeState.tileSize,
				showAlertTray: themeState.showAlertTray,
				viewMode: themeState.viewMode,
				tileSortOrder: themeState.tileSortOrder,
				tileColumns: themeState.tileColumns,
				tileMinHeight: themeState.tileMinHeight
			}).catch(() => {
				// Silent fail — localStorage already updated by ThemeContext
			});
		}, SAVE_DEBOUNCE_MS);

		return () => {
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
		};
	}, [themeState.darkMode, themeState.audioEnabled, themeState.fullscreen, themeState.tileSize, themeState.showAlertTray, themeState.viewMode, themeState.tileSortOrder, themeState.tileColumns, themeState.tileMinHeight, authState.loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

	return children;
};

PreferencesSyncProvider.propTypes = {
	children: PropTypes.node.isRequired
};

export default PreferencesSyncProvider;
